<template>
  <div class="condition-card">
    <!-- Tag row -->
    <div class="flex items-center gap-2 mb-2">
      <Tag size="small" :type="condition.satisfied ? 'success' : 'info'">
        {{ condition.satisfied ? $t('member.memberLevelProgress.met') : $t('member.memberLevelProgress.notMet') }}
      </Tag>
      <span class="text-sm text-primary-100">{{ isRenewal ? $t('member.memberLevelProgress.renewalCondition') : $t('member.memberLevelProgress.upgradeCondition') }}</span>
    </div>

    <!-- Title row -->
    <div class="flex justify-between items-center mb-2 text-gray-100 text-sub">
      <span class="font-medium ">{{ currentLevelName }}{{ $t('member.memberLevelProgress.memberLabel') }} {{ metricTypeName }}</span>
      <span>{{ valueDisplay }}</span>
    </div>

    <!-- Progress bar -->
    <MemberLevelProgressBar :percentage="condition.percentage" class="mb-2" />

    <!-- Gap text -->
    <p v-if="!customText && gapText" class="text-sub text-gray-80 mb-1">
      <template v-if="condition.satisfied">
        {{ $t('member.memberLevelProgress.conditionMetAction') }}{{ gapText.action }}
      </template>
      <template v-else>
        {{ gapText.expiredDateStr }} {{ $t('member.memberLevelProgress.spendBefore') }}
        <span class="font-bold text-primary-100">{{ gapText.gapDisplay }}</span>
        {{ gapText.action }}
      </template>
      <span class="font-bold">{{ gapText.targetLevelText }}</span>
    </p>
    <!-- Custom text -->
    <p v-if="customText" class="text-sub text-gray-80 mt-1">{{ customText }}</p>

    <!-- Recognized period -->
    <p v-if="shouldShowRecognizedPeriod" class="text-sub text-gray-60 flex items-center gap-2">
      <span>{{ $t('member.memberLevelProgress.recognizedPeriod') }}</span>
      <span>{{ recognizedPeriod }}</span>
    </p>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import Tag from '@/components/Tag/Tag.vue'
import MemberLevelProgressBar from './MemberLevelProgressBar.vue'
import { formatDate } from '@/utils/date'

export default defineComponent({
  name: 'ConditionCard',
  components: { Tag, MemberLevelProgressBar },
  props: {
    condition: { type: Object, required: true },
    currentLevelName: { type: String, default: '' },
    targetLevelName: { type: String, default: '' },
    isRenewal: { type: Boolean, default: false },
    expiredAt: { type: String, default: null },
    customText: { type: String, default: null },
  },
  setup (props) {
    const metricTypeName = computed(() => {
      const type = props.condition.metricType
      const isSpecific = props.condition.itemScope === 'specific'
      const judgmentLabel = props.condition.judgmentType === 'single' ? t('member.memberLevelProgress.singleJudgment') : t('member.memberLevelProgress.cumulativeJudgment')
      if (isSpecific) {
        if (type === 'totalAmount') return `${judgmentLabel}${t('member.memberLevelProgress.spendAmountSpecific')}`
        if (type === 'totalQuantity') return `${judgmentLabel}${t('member.memberLevelProgress.spendQuantitySpecific')}`
      }
      if (type === 'totalAmount') return `${judgmentLabel}${t('member.memberLevelProgress.spendAmount')}`
      if (type === 'totalQuantity') return `${judgmentLabel}${t('member.memberLevelProgress.spendQuantity')}`
      if (type === 'orderCount') return `${judgmentLabel}${t('member.memberLevelProgress.spendOrderCount')}`
      return type
    })

    const valueDisplay = computed(() => {
      const current = props.condition.currentValue
      const threshold = props.condition.threshold
      const type = props.condition.metricType
      if (type === 'totalAmount') {
        return `NT$ ${current.toLocaleString()} / ${threshold.toLocaleString()}`
      }
      if (type === 'totalQuantity') {
        return `${current} / ${threshold} ${t('member.memberLevelProgress.quantityUnit')}`
      }
      return `${current} / ${threshold} ${t('member.memberLevelProgress.countUnit')}`
    })

    const gapText = computed(() => {
      const gap = Math.max(props.condition.threshold - props.condition.currentValue, 0)
      const expiredDateStr = props.expiredAt ? formatDate(props.expiredAt, 'YYYY/MM/DD') : t('member.memberLevelProgress.permanent')
      const gapDisplay = props.condition.metricType === 'totalAmount'
        ? `NT$ ${gap.toLocaleString()}`
        : props.condition.metricType === 'totalQuantity'
          ? `${gap} ${t('member.memberLevelProgress.quantityUnit')}`
          : `${gap} ${t('member.memberLevelProgress.countUnit')}`
      const action = props.isRenewal ? t('member.memberLevelProgress.renewTo') : t('member.memberLevelProgress.upgradeTo')
      return {
        expiredDateStr,
        gapDisplay,
        action,
        targetLevelText: `${props.targetLevelName}${t('member.memberLevelProgress.memberLabel')}`,
      }
    })

    const recognizedPeriod = computed(() => {
      const start = props.condition.recognizedStartDate
      const end = props.condition.recognizedEndDate
      if (!start) return '-'
      const endStr = end ? formatDate(end, 'YYYY/MM/DD') : t('member.memberLevelProgress.permanent')
      return `${formatDate(start, 'YYYY/MM/DD')} ～ ${endStr}`
    })

    const shouldShowRecognizedPeriod = computed(() => {
      return props.condition.judgmentType !== 'single'
    })

    return { metricTypeName, valueDisplay, gapText, recognizedPeriod, shouldShowRecognizedPeriod }
  },
})
</script>

<style scoped lang="postcss">
.condition-card {
  @apply bg-white rounded-lg p-4 mb-3;
  box-shadow: 0px 1px 13px 0px #0000001A;
}
</style>
